<script setup lang="ts">
  // Vue core imports
  import type { PropType } from 'vue'
  import { computed, onBeforeUnmount, ref, watch } from 'vue'

  // External library imports

  import { Key01Icon } from '@gohighlevel/ghl-icons/24/outline'

  // Internal imports
  import CPButton from '@ui/components/CPButton.vue'

  import CPDivider from '@ui/components/CPDivider.vue'

  import CPInputOtp from '@ui/components/CPInputOtp.vue'

  import CPText from '@ui/components/CPText.vue'

  import { useI18n } from 'vue-i18n'
  import { createElementId } from '../../../core-layer/utils'

  import { useAuth } from '../../composables/useAuth'

  import { LoginEvents, Screens } from '../../types/auth'

  import {
    buildSetPasswordScreenChange,
    getErrorMessage,
    isDeferredInPlacePasswordLoginResult,
    shouldShowSetPasswordAfterLoginResponse,
  } from '../../utils'

  import Recaptcha from '../captcha/ReCaptcha.vue'

  // Props definitions
  const props = defineProps({
    title: {
      type: String,
      default: 'Login',
    },
    email: {
      type: String,
      required: false,
      default: '',
    },
    otpToken: {
      type: String,
      default: '',
    },
    password: {
      type: String,
      default: '',
    },
    username: {
      type: String,
      default: '',
    },
    locationId: {
      type: String,
      default: '',
    },
    defaultOtp: {
      type: String,
      default: null,
    },
    action: {
      type: String as PropType<'login' | 'signup'>,
      default: 'login',
    },
    referrerUser: {
      type: Object as PropType<{
        email: string
        name: string
        _id?: string
      }>,
      default: null,
      required: false,
    },
    /** Clientportal opt-in — defer session until after in-place Set Password. */
    deferSetPasswordSession: {
      type: Boolean,
      default: false,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    (e: 'changeScreen', value: any): void
    (e: 'loginCallback', value: any): void
  }>()

  const { t } = useI18n()

  // Refs and reactive variables
  const errorMessage = ref()

  const otpCode = ref('')

  const loading = ref(false)

  const showRecaptcha = ref(false)

  const reCaptchaToken = ref(undefined)

  const verifyDisabled = ref(false)

  const isResendButtonDisabled = ref(false)

  const debounceTimeout = ref<NodeJS.Timeout | null>(null)

  const resendTimeout = ref<NodeJS.Timeout | null>(null)

  // Computed properties
  const isOtpValid = computed(() => {
    return otpCode.value && otpCode.value.length === 6 && !errorMessage.value
  })

  // User-defined functions
  const handleOnChange = (value: string) => {
    otpCode.value = value
    if (value.length === 6) {
      handleOnComplete({ otp: value, state: 'complete' })
    }
  }

  const handleOnCompleteInternal = async ({ otp, state }: { otp: string; state: string }) => {
    otpCode.value = otp

    if (otpCode.value?.length !== 6) return

    loading.value = true
    try {
      if (!Number(otpCode.value)) {
        errorMessage.value = t('auth.validation.secureCodeNumeric')
        loading.value = false
        return
      }
      errorMessage.value = null
      const signupPageUrl = window.location.href
      const result = props.password
        ? await useAuth().signUp({
            email: props.email,
            otp: Number(otpCode.value),
            redirectUrl: window.location.search.match(/redirectUrl=([^&]*)/)?.[1] || '',
            password: props.password?.trim(),
            username: props.username?.trim(),
            captchaToken: reCaptchaToken.value,
            referredBy: props.referrerUser?._id,
            signupPageUrl: signupPageUrl,
            otpToken: props.otpToken,
          })
        : await useAuth().verifyOTPForLogin(props.email, props.action, Number(otpCode.value), props.otpToken, {
            deferSessionUntilInPlacePasswordSet: props.deferSetPasswordSession,
          })

      if (!props.password && isDeferredInPlacePasswordLoginResult(props.deferSetPasswordSession, result)) {
        emit('changeScreen', buildSetPasswordScreenChange(props.email, result.data))
        showRecaptcha.value = false
        loading.value = false
        return
      }

      const data = 'data' in result ? result.data : result

      if (data && 'token' in data && data.token) {
        if (shouldShowSetPasswordAfterLoginResponse(data)) {
          emit('changeScreen', buildSetPasswordScreenChange(props.email, data))
        } else {
          emit('loginCallback', {
            eventType: LoginEvents.LOGIN_SUCCESSFUL,
            ...data,
          })
        }
        loading.value = false
      }
      showRecaptcha.value = false
      loading.value = false
    } catch (error: any) {
      if (error?.response?.status === 429) {
        verifyDisabled.value = true
        showRecaptcha.value = true
      } else {
        showRecaptcha.value = false
        console.log('error', error, error.message)
        errorMessage.value = getErrorMessage(error)
        //If user not found
        if (errorMessage.value == 'User not found') {
          emit('changeScreen', {
            email: props.email,
            page: Screens.SIGN_UP,
          })
        }
      }
      loading.value = false
    }
  }

  // Create debounced version of handleOnComplete
  const handleOnComplete = debounce(handleOnCompleteInternal, 20, debounceTimeout)

  const onVerify = (response: any) => {
    reCaptchaToken.value = response
    verifyDisabled.value = false
  }

  const onError = (response: any) => {
    verifyDisabled.value = true
  }

  const onExpired = (response: any) => {
    reCaptchaToken.value = undefined
    verifyDisabled.value = true
  }

  onMounted(() => {
    console.log('props', props.otpToken)
  })

  // Cleanup timeouts on unmount
  onBeforeUnmount(() => {
    if (debounceTimeout.value) {
      clearTimeout(debounceTimeout.value)
    }
    if (resendTimeout.value) {
      clearTimeout(resendTimeout.value)
    }
  })

  // Simple debounce function
  function debounce<T extends (...args: any[]) => any>(func: T, wait: number, timeoutRef: { value: NodeJS.Timeout | null }): T {
    return ((...args: any[]) => {
      if (timeoutRef.value) {
        clearTimeout(timeoutRef.value)
      }
      timeoutRef.value = setTimeout(() => func(...args), wait)
    }) as T
  }

  async function resendOtp() {
    if (isResendButtonDisabled.value) {
      return
    }

    try {
      const { data } = await useAuth().sendOtp({
        type: 'resendOtp',
        email: props.email,
        captchaToken: reCaptchaToken.value,
        fullName: props.username,
        action: props.action,
      })
      emit('changeScreen', {
        page: Screens.VERIFY_SECURE_CODE,
        email: props?.email,
        otpToken: data?.otpToken,
        password: props?.password?.trim(),
        username: props?.username?.trim(),
        locationId: props.locationId,
      })
      isResendButtonDisabled.value = true
      if (resendTimeout.value) {
        clearTimeout(resendTimeout.value)
      }
      resendTimeout.value = setTimeout(() => (isResendButtonDisabled.value = false), 30000)
    } catch (error: any) {
      if (error?.response?.status === 429) {
        verifyDisabled.value = true
        isResendButtonDisabled.value = true
        showRecaptcha.value = true
      } else {
        errorMessage.value = getErrorMessage(error)
        showRecaptcha.value = false
      }
    }
  }

  // Watch effects
  watch(otpCode, () => (errorMessage.value = null))

  // Push empty state to prevent browser back navigation issues
  history.pushState({}, '', '')
</script>

<template>
  <div class="flex flex-col items-center">
    <div class="w-full">
      <div class="mb-1 flex items-center justify-between">
        <CPText size="md" weight="medium" class="text-body">
          {{ t('auth.secureCode') }}
          <span class="text-danger">*</span>
        </CPText>
        <CPText
          :id="createElementId('secure-code', 'modal', 'resend-code-button')"
          size="sm"
          weight="semibold"
          class="text-primary"
          :class="
            isResendButtonDisabled ? 'cursor-not-allowed pointer-events-none opacity-50 select-none' : 'cursor-pointer hover:underline'
          "
          role="button"
          :tabindex="isResendButtonDisabled ? -1 : 0"
          :aria-disabled="isResendButtonDisabled"
          @click="resendOtp"
          @keydown.enter.prevent="resendOtp"
          @keydown.space.prevent="resendOtp"
        >
          {{ t('auth.resendCodeToEmail') }}
        </CPText>
      </div>

      <div class="mb-1 flex w-full items-center justify-center">
        <CPInputOtp
          :id="createElementId('secure-code', 'modal', 'otp-input')"
          :fields="6"
          size="sm"
          :separator-position="3"
          class="w-full"
          @onComplete="handleOnComplete"
          @onChange="handleOnChange"
        />
      </div>

      <div v-if="errorMessage" class="text-danger mb-3 w-full">
        <CPText size="sm">{{ errorMessage }}</CPText>
      </div>
      <CPText v-else size="sm" class="text-clientportal-primary mb-3">
        {{ t('auth.enterSecureCodeHint') }}
      </CPText>

      <Recaptcha
        v-if="showRecaptcha"
        :id="createElementId('secure-code', 'modal', 'recaptcha')"
        class="mb-4 flex w-full justify-center"
        @expired="onExpired"
        @verify="onVerify"
        @error="onError"
      />

      <CPButton
        :id="createElementId('secure-code', 'modal', 'verify-button')"
        class="my-4 w-full rounded-lg"
        type="primary"
        size="md"
        :loading="loading"
        :disabled="loading || verifyDisabled || !isOtpValid"
        @click="handleOnComplete({ otp: otpCode, state: 'complete' })"
      >
        {{ t('auth.verifySecureCode') }}
        <template #iconLeft>
          <Key01Icon class="w-4 h-4" />
        </template>
      </CPButton>

      <div class="mb-4 flex w-full items-center justify-center">
        <CPDivider class="flex-1" margin-bottom="0" margin-top="0">{{ t('auth.or') }}</CPDivider>
      </div>

      <CPButton
        :id="createElementId('secure-code', 'modal', 'login-with-password-button')"
        class="w-full rounded-lg"
        type="secondary"
        variant="secondary"
        color="secondary"
        size="md"
        @click="emit('changeScreen', { page: Screens.LOGIN })"
      >
        {{ t('auth.loginWithPassword') }}
      </CPButton>
    </div>
  </div>
</template>

<style scoped>
  .hr-divider:not(.hr-divider--vertical) {
    margin: 0px;
  }
</style>
